import React, { useState } from 'react';
import { BusinessPlan } from '../../types/businessPlan';
import { ENV } from '../../config/env';
import { supabase } from '../../config/supabaseClient';

// API base URL
const API_BASE_URL = `${ENV.API_URL}/api`;

interface BusinessPlanPDFExportProps {
  plan: BusinessPlan;
  onPdfGenerated?: (blob: Blob, fileName: string) => Promise<void> | void;
  variant?: 'panel' | 'button';
}

const embedCanvasChartsAsImages = (source: HTMLElement, clone: HTMLElement) => {
  try {
    const sourceCanvases = Array.from(source.querySelectorAll('canvas')) as HTMLCanvasElement[];
    const cloneCanvases = Array.from(clone.querySelectorAll('canvas')) as HTMLCanvasElement[];

    const count = Math.min(sourceCanvases.length, cloneCanvases.length);
    for (let i = 0; i < count; i++) {
      const srcCanvas = sourceCanvases[i];
      const dstCanvas = cloneCanvases[i];

      try {
        const dataUrl = srcCanvas.toDataURL('image/png');
        if (!dataUrl) continue;

        const img = document.createElement('img');
        img.src = dataUrl;
        img.alt = 'Chart';
        img.style.maxWidth = '100%';
        img.style.height = 'auto';

        dstCanvas.parentNode?.replaceChild(img, dstCanvas);
      } catch (e) {
      }
    }
  } catch (e) {
  }
};

interface ThemeOption {
  id: string;
  name: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
    text: string;
    background: string;
  };
}

interface ChartData {
  id: string;
  config: any; // Chart.js config object
}

const BusinessPlanPDFExport: React.FC<BusinessPlanPDFExportProps> = ({ plan, onPdfGenerated, variant = 'panel' }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTheme, setSelectedTheme] = useState('default');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [customColors, setCustomColors] = useState({
    primary: '#1a73e8',
    secondary: '#f1f3f4',
    accent: '#fbbc04',
    text: '#333333',
    background: '#ffffff'
  });

  // Predefined theme options
  const themeOptions: ThemeOption[] = [
    { 
      id: 'default', 
      name: 'Professional Blue', 
      colors: {
        primary: '#1a73e8',
        secondary: '#f1f3f4',
        accent: '#fbbc04',
        text: '#333333',
        background: '#ffffff'
      }
    },
    { 
      id: 'elegant', 
      name: 'Elegant Green', 
      colors: {
        primary: '#0f8b8d',
        secondary: '#f8f9fa',
        accent: '#ffc107',
        text: '#2e3440',
        background: '#ffffff'
      }
    },
    { 
      id: 'modern', 
      name: 'Modern Gray', 
      colors: {
        primary: '#5f6368',
        secondary: '#f1f3f4',
        accent: '#ea4335',
        text: '#202124',
        background: '#ffffff'
      }
    },
    { 
      id: 'bold', 
      name: 'Bold Red', 
      colors: {
        primary: '#db4437',
        secondary: '#f5f5f5',
        accent: '#4285f4',
        text: '#333333',
        background: '#ffffff'
      }
    },
    { 
      id: 'custom', 
      name: 'Custom Colors', 
      colors: customColors
    }
  ];

  const removeChartsFromClone = (root: HTMLElement) => {
    // Target business-plan specific chart areas only
    const nodes = root.querySelectorAll(
      '.financial-charts, .market-analysis-charts, .chart-container, canvas'
    );
    nodes.forEach((n) => n.parentNode?.removeChild(n));
  };

  const prepareContentForPDF = (contentElement: HTMLElement): string => {
    const contentClone = contentElement.cloneNode(true) as HTMLElement;

    // Remove obvious UI-only controls (PDF templates also hide these via CSS).
    const uiOnly = contentClone.querySelectorAll(
      '.edit-section-btn, button.edit-section-btn, [data-tooltip-id], .react-tooltip'
    );
    uiOnly.forEach((n) => n.parentNode?.removeChild(n));

    return contentClone.innerHTML;
  };

  const fetchPdf = async () => {
    try {
      setIsExporting(true);
      setLoading(true);
      setError(null);
      
      // Get the business plan content element - Try multiple selectors
      const contentElement = document.getElementById('business-plan-document') || 
                             document.querySelector('.business-plan-document') ||
                             document.querySelector('.document-content') ||
                             document.querySelector('.business-plan');
                             
      if (!contentElement) {
        throw new Error('Could not find business plan content element. Please ensure the plan is visible.');
      }
      
      console.log('Found content element:', contentElement.tagName, contentElement.id || 'no-id', 
        'with', contentElement.children.length, 'children');
      
      // Prepare the content by removing UI elements and formatting for PDF
      // Clone the element and optionally remove charts in the clone (do NOT mutate live DOM)
      const cloned = contentElement.cloneNode(true) as HTMLElement;
      if (!includeCharts) {
        removeChartsFromClone(cloned);
      } else {
        // Canvas bitmaps do not clone; copy toDataURL from live DOM into the cloned export
        embedCanvasChartsAsImages(contentElement, cloned);
      }

      const content = prepareContentForPDF(cloned);
      
      // Get the session token for authentication
      const sessionResp = await supabase.auth.getSession();
      const token = sessionResp?.data?.session?.access_token || '';
      
      // Create the request options
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const options = {
        method: 'POST',
        headers,
        body: JSON.stringify({
          htmlContent: content, // Fix parameter name to match backend expectation
          title: plan.title,
          theme: selectedTheme,
          colors: customColors,
          charts: [] // Include empty charts array to satisfy backend requirement
        })
      };
      
      // Make the request to generate the PDF
      const response = await fetch(`${API_BASE_URL}/business-plans/generate-pdf`, options);
      
      if (!response.ok) {
        throw new Error(`PDF generation failed: ${response.statusText}`);
      }
      
      // Get the PDF as a blob
      const blob = await response.blob();

      // Optionally notify parent with the blob
      const fileName = `${plan.title || 'Business Plan'}.pdf`;
      try {
        if (onPdfGenerated) {
          await onPdfGenerated(blob, fileName);
        }
      } catch (callbackErr) {
        console.warn('onPdfGenerated callback failed:', callbackErr);
      }

      // Create a URL for the blob and trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setIsExporting(false);
      setLoading(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError(error instanceof Error ? error.message : 'An unknown error occurred');
      setIsExporting(false);
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (loading) return;
    
    setIsExporting(true);
    await fetchPdf();
    setIsExporting(false);
  };

  const panel = (
    <div className="business-plan-export">
      <div className="mb-4">
        <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={includeCharts}
            onChange={(e) => setIncludeCharts(e.target.checked)}
            disabled={isExporting}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>Include charts</span>
        </label>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Document Theme</label>
        <select 
          value={selectedTheme}
          onChange={(e) => setSelectedTheme(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          disabled={isExporting}
        >
          {themeOptions.map(theme => (
            <option key={theme.id} value={theme.id}>{theme.name}</option>
          ))}
        </select>
      </div>
      
      {selectedTheme === 'custom' && (
        <div className="mb-4 p-3 border border-gray-200 rounded-md bg-gray-50">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Custom Colors</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Primary Color</label>
              <div className="flex items-center">
                <input 
                  type="color" 
                  value={customColors.primary}
                  onChange={(e) => setCustomColors({...customColors, primary: e.target.value})}
                  className="w-8 h-8 mr-2 border border-gray-300 rounded"
                  disabled={isExporting}
                />
                <input 
                  type="text" 
                  value={customColors.primary}
                  onChange={(e) => setCustomColors({...customColors, primary: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md"
                  disabled={isExporting}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Secondary Color</label>
              <div className="flex items-center">
                <input 
                  type="color" 
                  value={customColors.secondary}
                  onChange={(e) => setCustomColors({...customColors, secondary: e.target.value})}
                  className="w-8 h-8 mr-2 border border-gray-300 rounded"
                  disabled={isExporting}
                />
                <input 
                  type="text" 
                  value={customColors.secondary}
                  onChange={(e) => setCustomColors({...customColors, secondary: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md"
                  disabled={isExporting}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Accent Color</label>
              <div className="flex items-center">
                <input 
                  type="color" 
                  value={customColors.accent}
                  onChange={(e) => setCustomColors({...customColors, accent: e.target.value})}
                  className="w-8 h-8 mr-2 border border-gray-300 rounded"
                  disabled={isExporting}
                />
                <input 
                  type="text" 
                  value={customColors.accent}
                  onChange={(e) => setCustomColors({...customColors, accent: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md"
                  disabled={isExporting}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Text Color</label>
              <div className="flex items-center">
                <input 
                  type="color" 
                  value={customColors.text}
                  onChange={(e) => setCustomColors({...customColors, text: e.target.value})}
                  className="w-8 h-8 mr-2 border border-gray-300 rounded"
                  disabled={isExporting}
                />
                <input 
                  type="text" 
                  value={customColors.text}
                  onChange={(e) => setCustomColors({...customColors, text: e.target.value})}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md"
                  disabled={isExporting}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="my-2 p-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}
      
      <button
        onClick={handleExport}
        disabled={isExporting}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline transition duration-150 ease-in-out disabled:opacity-50"
      >
        {isExporting ? (
          <>
            <span className="inline-block animate-spin mr-2">⟳</span>
            Generating PDF...
          </>
        ) : (
          'Export PDF'
        )}
      </button>
    </div>
  );

  if (variant === 'button') {
    return (
      <>
        <button
          onClick={() => setShowModal(true)}
          className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
          disabled={isExporting}
          title="Export to PDF"
        >
          Export PDF
        </button>

        {showModal ? (
          <>
            <div
              className="fixed inset-0 z-[9998] bg-black/30"
              onClick={() => setShowModal(false)}
            />
            <div
              className="fixed left-1/2 top-1/2 z-[9999] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-4 shadow-2xl ring-1 ring-gray-200"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-gray-900">Export PDF</div>
                <button
                  onClick={() => setShowModal(false)}
                  className="rounded-md p-2 text-gray-500 transition hover:bg-gray-100"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4">{panel}</div>
            </div>
          </>
        ) : null}
      </>
    );
  }

  return panel;
};

// Add Chart type to the global Window interface
declare global {
  interface Window {
    Chart?: {
      getChart?: (canvas: HTMLCanvasElement) => any;
    };
  }
}

export default BusinessPlanPDFExport;
